/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, race, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { easeOutCubic } from '../scrolling/easing-function';
import { ScrollPlayer } from '../scrolling/scroll-player';
import { getScrollSnapBoundingRect } from '../scrolling/scrolling';
import { ScrollCoordinates } from '../scrolling/scrolling.types';
import { FocusManager } from '../services/focus-manager';
import { isHorizontalDir, isVerticalDir } from '../services/spatial-navigation/spatial-navigation';
import {
    NavigationEvent,
    SpatialNavigationDirection
} from '../services/spatial-navigation/spatial-navigation-polyfill';
import { ContinuousNavigationMotionDirective } from './continuous-navigation-motion.directive';

const ScrollDuration = 96;

export type Orientation = 'horizontal' | 'vertical';

interface FocusNavigation {
    readonly direction: SpatialNavigationDirection;
    readonly target: HTMLElement;
    readonly scroller: HTMLElement;
    readonly destination?: ScrollCoordinates;
}

@Directive()
export abstract class BaseFocusThenScrollDirective implements OnDestroy, AfterViewInit {
    @Input() spacingTop = 0;
    @Input() spacingBottom = 0;
    @Input() spacingLeft = 0;
    @Input() spacingRight = 0;
    @Input() topInset = 0;
    @Input() bottomInset = 0;

    @Input('enableFocusThenScroll') get enabled(): boolean {
        return this._enabled;
    }
    set enabled(value: any) {
        this._enabled = coerceBooleanProperty(value);
    }
    private _enabled = true;

    @Input() get enableKeyThrottle() {
        return this._enableKeyThrottle;
    }
    set enableKeyThrottle(value) {
        this._enableKeyThrottle = coerceBooleanProperty(value);
    }
    private _enableKeyThrottle = true;

    private readonly cancelScrolling$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();
    private isScrolling = false;

    protected abstract get orientation(): Orientation;
    protected abstract get scrollerElement(): HTMLElement;

    constructor(
        protected elementRef: ElementRef<HTMLElement>,
        private readonly focusManager: FocusManager,
        private readonly ngZone: NgZone,
        private readonly continuousNavigationMotion: ContinuousNavigationMotionDirective | null
    ) {}

    ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.elementRef.nativeElement.addEventListener('navbeforefocus', this.navBeforeFocus);
        });
    }

    ngOnDestroy(): void {
        this.continuousNavigationMotion?.cancel();
        this.destroy$.next();
        this.destroy$.complete();
        this.elementRef.nativeElement.removeEventListener('navbeforefocus', this.navBeforeFocus);
    }

    private readonly navBeforeFocus = (event: NavigationEvent): void => {
        const motionEnabled = !!this.continuousNavigationMotion?.enabled;
        const navigation = this.prepareNavigation(event, motionEnabled);
        if (!navigation) {
            return;
        }

        if (motionEnabled) {
            event.preventDefault();
            event.stopPropagation();
            this.cancelLegacyNavigation();
            this.continuousNavigationMotion!.start({
                direction: navigation.direction,
                scroller: navigation.scroller,
                destination: navigation.destination,
                canContinue: () => this.enabled
            });
            this.focusManager.focusViaLastOrigin(navigation.target, { preventScroll: true });
            return;
        }

        if (!navigation.destination) {
            return;
        }

        event.preventDefault();
        this.focusManager.focusViaLastOrigin(navigation.target, { preventScroll: true });
        this.cancelLegacyNavigation();

        const scrollPlayer = new ScrollPlayer(navigation.scroller, navigation.destination.x, navigation.destination.y, {
            easingFunction: easeOutCubic,
            duration: ScrollDuration
        });
        if (this.enableKeyThrottle) {
            fromEvent(navigation.target, 'keydown', { capture: true })
                .pipe(takeUntil(race(scrollPlayer.onScrollEnd(), this.cancelScrolling$, this.destroy$)))
                .subscribe(this.swallowKeydownEvent);
        }
        this.doScrolling(scrollPlayer);
    };

    private prepareNavigation(event: NavigationEvent, motionEnabled: boolean): FocusNavigation | undefined {
        const target = event.target;
        if (!this.enabled || event.defaultPrevented || !(target instanceof HTMLElement)) {
            return undefined;
        }

        const scroller = this.scrollerElement;
        const orientation = this.orientation;
        const validDirection =
            orientation === 'horizontal' ? isHorizontalDir(event.detail.dir) : isVerticalDir(event.detail.dir);
        if (
            !scroller ||
            !orientation ||
            !scroller.contains(target) ||
            (motionEnabled && (!validDirection || target === this.elementRef.nativeElement))
        ) {
            return undefined;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const targetRect = getScrollSnapBoundingRect(target);
        if (this.isVisibleInScroller(targetRect, scrollerRect, orientation)) {
            return { direction: event.detail.dir, target, scroller };
        }

        const scrollAmount = this.calculateRequiredScrollAmount(targetRect, scrollerRect, orientation);
        return {
            direction: event.detail.dir,
            target,
            scroller,
            destination: scrollAmount === 0 ? undefined : this.getDestinationCoordinates(scrollAmount)
        };
    }

    private cancelLegacyNavigation(): void {
        if (this.isScrolling) {
            this.cancelScrolling$.next();
            this.isScrolling = false;
        }
    }

    private isVisibleInScroller(elementRect: DOMRect, scrollerRect: DOMRect, orientation: Orientation): boolean {
        return orientation === 'horizontal'
            ? elementRect.left >= scrollerRect.left && elementRect.right <= scrollerRect.right
            : elementRect.top >= scrollerRect.top + this.topInset &&
                  elementRect.bottom <= scrollerRect.bottom - this.bottomInset;
    }

    private calculateRequiredScrollAmount(
        elementRect: DOMRect,
        scrollerRect: DOMRect,
        orientation: Orientation
    ): number {
        if (orientation === 'horizontal') {
            return elementRect.left < scrollerRect.left
                ? elementRect.left - this.spacingLeft - scrollerRect.left
                : elementRect.right + this.spacingRight - scrollerRect.right;
        }
        return elementRect.top < scrollerRect.top + this.topInset
            ? elementRect.top - this.spacingTop - scrollerRect.top - this.topInset
            : elementRect.bottom + this.spacingBottom - scrollerRect.bottom + this.bottomInset;
    }

    private getDestinationCoordinates(scrollAmount: number): ScrollCoordinates {
        return this.orientation === 'horizontal'
            ? { x: this.scrollerElement.scrollLeft + scrollAmount, y: this.scrollerElement.scrollTop }
            : { x: this.scrollerElement.scrollLeft, y: this.scrollerElement.scrollTop + scrollAmount };
    }

    private readonly swallowKeydownEvent = (event: KeyboardEvent): void => {
        event.stopPropagation();
        event.preventDefault();
    };

    private doScrolling(scrollPlayer: ScrollPlayer): void {
        scrollPlayer
            .onScrollEnd()
            .pipe(take(1), takeUntil(race(this.destroy$, this.cancelScrolling$)))
            .subscribe(() => (this.isScrolling = false));
        scrollPlayer
            .onScrollStart()
            .pipe(take(1), takeUntil(race(this.destroy$, this.cancelScrolling$)))
            .subscribe(() => (this.isScrolling = true));
        scrollPlayer
            .start()
            .pipe(takeUntil(race(this.cancelScrolling$, this.destroy$)))
            .subscribe();
    }
}
